import Highcharts from 'highcharts';

/**
 * Give every Highcharts chart element-level resize detection.
 *
 * Highcharts' built-in auto-resize binds to one event and one only:
 * `Chart.prototype.setReflow` does `addEvent(window, 'resize', ...)`, and the
 * library ships no `ResizeObserver`. A chart therefore re-renders when the
 * *browser window* changes size and at no other time — so any layout change
 * that narrows a chart's container on its own leaves the SVG at its previous
 * width, clipped inside the smaller container.
 *
 * The Ask Moo sidebar is one such change: `ResizableSidebar` publishes
 * `--ask-moo-sidebar-width` on `<html>` and `.main-root-wrapper` narrows via
 * `calc()` (see `GlobalStyles`), which is pure CSS and fires no window resize.
 * It is not the only one, which is why this is fixed here rather than at that
 * one call site — collapsing nav, opening panels and tab switches all reshape
 * chart containers the same way.
 *
 * Registering on the `Chart` *class* rather than per instance means all charts
 * — the 17 files using `HighchartsReact` today, and any added later — get this
 * with no call-site changes.
 */

/** Charts we are currently observing. Also acts as the liveness check for the
 *  resize callback: a destroyed chart is removed here before its observer is
 *  disconnected, so a callback already in flight cannot reflow a dead chart. */
const observed = new WeakMap<Highcharts.Chart, ResizeObserver>();

let installed = false;

function startObserving(chart: Highcharts.Chart): void {
  // jsdom (tests) and older browsers have no ResizeObserver. Charts simply
  // keep the stock window-resize behavior there.
  if (typeof ResizeObserver === 'undefined') return;

  // `chart.reflow: false` is how Highcharts lets a chart opt out of automatic
  // resizing. `Chart.prototype.reflow` itself does not check it -- only the
  // window listener it guards is skipped -- so observing such a chart would
  // quietly reinstate the behavior it opted out of. No chart in this app sets
  // it today; honor it so one added later behaves as its author expects.
  if (chart.options?.chart?.reflow === false) return;

  // `chart.container` is a div Highcharts creates *inside* the element it was
  // told to render into, and Highcharts writes an explicit pixel width onto
  // it. Observing it would feed our own reflow back in as a resize; its
  // parent is the element whose width layout actually decides.
  const renderTo = chart.container?.parentElement;
  if (!renderTo || observed.has(chart)) return;

  const observer = new ResizeObserver(() => {
    if (!observed.has(chart)) return;
    // Must be called with no argument: Highcharts' own `reflow(e)` bails out
    // unless `e.target` is window or document. Called bare it measures the
    // container and returns early when the size is unchanged, so the initial
    // delivery ResizeObserver makes on `observe()` costs nothing.
    chart.reflow();
  });

  observer.observe(renderTo);
  observed.set(chart, observer);
}

function stopObserving(chart: Highcharts.Chart): void {
  const observer = observed.get(chart);
  if (!observer) return;
  observed.delete(chart);
  observer.disconnect();
}

export function installHighchartsContainerReflow(): void {
  if (installed) return;
  installed = true;

  Highcharts.addEvent(
    Highcharts.Chart,
    'load',
    function onChartLoad(this: Highcharts.Chart) {
      startObserving(this);
    }
  );

  Highcharts.addEvent(
    Highcharts.Chart,
    'destroy',
    function onChartDestroy(this: Highcharts.Chart) {
      stopObserving(this);
    }
  );
}
